import { lazy, Suspense, useEffect, type ComponentType, type LazyExoticComponent } from 'react';
import { Route, Switch, Router as WouterRouter, useLocation } from 'wouter';
import { DEFAULT_LOCALE, localeFromPath, LocaleProvider, type Locale } from '@/lib/locale';

// Eager — always needed immediately
import Home from '@/pages/Home';

// Lazy — loaded on demand (code splitting).
// lazyRetry: se un chunk non si carica (es. dopo un deploy i vecchi file
// non esistono più), ricarica la pagina una volta invece di mostrare
// una pagina bianca.
function lazyRetry<T extends { default: ComponentType<any> }>(
  factory: () => Promise<T>,
): LazyExoticComponent<T['default']> {
  return lazy(() =>
    factory().catch((error) => {
      const key = 'chunk-reload-' + window.location.pathname;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        // Mai risolta: la pagina si sta ricaricando
        return new Promise<T>(() => {});
      }
      sessionStorage.removeItem(key);
      throw error;
    }),
  );
}

const Features           = lazyRetry(() => import('@/pages/Features'));
const AlloggiatiWeb      = lazyRetry(() => import('@/pages/AlloggiatiWeb'));
const BookingEngine      = lazyRetry(() => import('@/pages/BookingEngine'));
const Contact            = lazyRetry(() => import('@/pages/Contact'));
const Privacy            = lazyRetry(() => import('@/pages/legal/Privacy'));
const Terms              = lazyRetry(() => import('@/pages/legal/Terms'));
const Cookie             = lazyRetry(() => import('@/pages/legal/Cookie'));
const Pricing            = lazyRetry(() => import('@/pages/Pricing'));
const NotFound           = lazyRetry(() => import('@/pages/not-found'));

const CheckinDigitale    = lazyRetry(() => import('@/pages/features/CheckinDigitale'));
const CheckoutQr         = lazyRetry(() => import('@/pages/features/CheckoutQr'));
const PrezziDinamici     = lazyRetry(() => import('@/pages/features/PrezziDinamici'));
const GestioneManutenzioni = lazyRetry(() => import('@/pages/features/GestioneManutenzioni'));
const RuoliPermessi      = lazyRetry(() => import('@/pages/features/RuoliPermessi'));
const Reportistica       = lazyRetry(() => import('@/pages/features/Reportistica'));
const AutomazioneEmail   = lazyRetry(() => import('@/pages/features/AutomazioneEmail'));
const SicurezzaBackup    = lazyRetry(() => import('@/pages/features/SicurezzaBackup'));
const ICalIntegration    = lazyRetry(() => import('@/pages/features/ICalIntegration'));
const Integrazioni       = lazyRetry(() => import('@/pages/features/Integrazioni'));
const Videoverifica      = lazyRetry(() => import('@/pages/features/Videoverifica'));
const MappaInterattiva   = lazyRetry(() => import('@/pages/features/MappaInterattiva'));
const ControlloAccessiSbarra = lazyRetry(() => import('@/pages/features/ControlloAccessiSbarra'));
const ReceptionRemota = lazyRetry(() => import('@/pages/features/ReceptionRemota'));
const GestionaleAreeSostaCamper = lazyRetry(() => import('@/pages/GestionaleAreeSostaCamper'));
const CosEOvvoCamping    = lazyRetry(() => import('@/pages/CosEOvvoCamping'));
const Team               = lazyRetry(() => import('@/pages/Team'));
const Tecnologia         = lazyRetry(() => import('@/pages/Tecnologia'));
const Blog               = lazyRetry(() => import('@/pages/Blog').then(m => ({ default: m.Blog })));
const BlogPost           = lazyRetry(() => import('@/pages/BlogPost').then(m => ({ default: m.BlogPost })));
const AnalyticsDashboard = lazyRetry(() => import('@/pages/AnalyticsDashboard'));
const Novita             = lazyRetry(() => import('@/pages/Novita'));
const TurismoNatura2026  = lazyRetry(() => import('@/pages/TurismoNatura2026'));
const GestionaleAgricampeggi = lazyRetry(() => import('@/pages/GestionaleAgricampeggi'));
const GestionaleGlamping = lazyRetry(() => import('@/pages/GestionaleGlamping'));
const AssistenteAiCampeggio = lazyRetry(() => import('@/pages/AssistenteAiCampeggio'));
const CollegareGestionaleSbarre = lazyRetry(() => import('@/pages/CollegareGestionaleSbarre'));

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    let observer: MutationObserver | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const scroll = () => {
      observer?.disconnect();
      clearTimeout(timer);
      if (!window.location.hash) {
        window.scrollTo(0, 0);
        return;
      }
      let id: string;
      try { id = decodeURIComponent(window.location.hash.slice(1)); }
      catch { return; }
      const findTarget = () => {
        const target = document.getElementById(id);
        if (!target) return false;
        target.scrollIntoView({ block: "start" });
        observer?.disconnect();
        clearTimeout(timer);
        return true;
      };
      // Lazy routes may not have mounted when navigation changes the hash.
      if (!findTarget()) {
        observer = new MutationObserver(findTarget);
        observer.observe(document.getElementById("root")!, { childList: true, subtree: true });
        timer = setTimeout(() => observer?.disconnect(), 10000);
      }
    };
    scroll();
    const events = ["hashchange", "popstate", "pushState", "replaceState"];
    events.forEach(event => window.addEventListener(event, scroll));
    return () => {
      observer?.disconnect();
      clearTimeout(timer);
      events.forEach(event => window.removeEventListener(event, scroll));
    };
  }, [location]);
  return null;
}


function Router() {
  return (
    <Suspense fallback={<div className="min-h-screen" />}>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/funzionalita" component={Features} />
        <Route path="/prezzi" component={Pricing} />
        <Route path="/alloggiati-web" component={AlloggiatiWeb} />
        <Route path="/prenotazioni-online" component={BookingEngine} />

        <Route path="/check-in-digitale" component={CheckinDigitale} />
        <Route path="/check-out-qr-code" component={CheckoutQr} />
        <Route path="/prezzi-dinamici" component={PrezziDinamici} />
        <Route path="/gestione-manutenzioni" component={GestioneManutenzioni} />
        <Route path="/ruoli-permessi" component={RuoliPermessi} />
        <Route path="/reportistica" component={Reportistica} />
        <Route path="/automazione-email" component={AutomazioneEmail} />
        <Route path="/sicurezza-backup" component={SicurezzaBackup} />
        <Route path="/sincronizzazione-ical" component={ICalIntegration} />
        <Route path="/integrazioni" component={Integrazioni} />
        <Route path="/controllo-accessi-sbarra" component={ControlloAccessiSbarra} />
        <Route path="/come-collegare-gestionale-sbarre" component={CollegareGestionaleSbarre} />
        <Route path="/reception-remota" component={ReceptionRemota} />
        <Route path="/gestionale-aree-sosta-camper" component={GestionaleAreeSostaCamper} />
        <Route path="/gestionale-agricampeggi" component={GestionaleAgricampeggi} />
        <Route path="/gestionale-glamping" component={GestionaleGlamping} />
        <Route path="/assistente-ai-campeggio" component={AssistenteAiCampeggio} />
        <Route path="/videoverifica" component={Videoverifica} />
        <Route path="/mappa-interattiva" component={MappaInterattiva} />

        <Route path="/cos-e-ovvo-camping" component={CosEOvvoCamping} />
        <Route path="/team" component={Team} />
        <Route path="/tecnologia" component={Tecnologia} />

        <Route path="/blog" component={Blog} />
        <Route path="/blog/:slug" component={BlogPost} />
        <Route path="/novita" component={Novita} />
        <Route path="/turismo-natura-2026" component={TurismoNatura2026} />

        <Route path="/contatti" component={Contact} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/termini" component={Terms} />
        <Route path="/cookie" component={Cookie} />
        <Route path="/stats" component={AnalyticsDashboard} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App({ ssrPath }: { ssrPath?: string }) {
  const rawPath = ssrPath ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  const locale: Locale = localeFromPath(rawPath);
  const basePath = import.meta.env.BASE_URL.replace(/\/$/, '');
  const localeBase = locale === DEFAULT_LOCALE ? '' : `/${locale}`;
  return (
    <LocaleProvider locale={locale}>
      <WouterRouter
        base={`${basePath}${localeBase}`}
        ssrPath={ssrPath}
      >
        <ScrollToTop />
        <Router />
      </WouterRouter>
    </LocaleProvider>
  );
}

export default App;
